import { RootState, store } from "../store";
import { handleUnauthorizedError } from "./apiMiddleware";

// URL de base pour l'API
const API_BASE_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}/orders`;

// Fonction pour récupérer le token d'authentification depuis le store Redux
const getAuthToken = (): string | null => {
  const state = store.getState() as RootState;
  return state.user.currentUser?.token || null;
};

// Fonction pour générer les en-têtes avec le token d'authentification
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Types pour les bons de commande
export interface DeliveryAddress {
  name: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  locationId?: string;
  deliveryInfo?: string;
  comment?: string;
}

export interface PurchaseOrderItem {
  productId: string;
  productName: string;
  vendor: string;
  sku?: string;
  barcode?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  supplierUnitPrice?: number;
  supplierTotalPrice?: number;
  locationDetails?: { [key: string]: number };
   unitWeightKg?: number;
   hsCode?: string;
   madeIn?: string;
  batches?: {
    batchNumber: string;
    expirationDate?: Date;
    quantity: number;
  }[];
}

export interface PurchaseOrder {
  _id?: string;
  reference?: string;
  externalId?: string;
  supplier: string;
  supplierId?: string;
  tvaRate?: 0 | 5.5 | 10 | 20;
  paymentDelayDays?: number;
  deliveryAddressId?: string;
  deliveryAddress: DeliveryAddress;
  items: PurchaseOrderItem[];
  orderDate?: Date;
  deliveryDate?: Date;
  expectedDeliveryDate?: Date;
  totalAmount: number;
  importCost?: number; // Coût d'importation/expédition (aligné avec le backend)
  shippingCost?: number; // Pour compatibilité avec le code existant (sera remplacé par importCost)
  estimatedCost?: number; // Frais de livraison estimés (heuristique puis devis transporteur)
  status?:
    | "draft"
    | "sent"
    | "partially_received"
    | "in_delivery"
    | "noted_delivered"
    | "received"
    | "completed"
    | "cancelled"
    | "dispute";
  notes?: string;
  locationName: string;
  locationId: string;
  retailerId?: string;
  retailerName?: string;
  retrocessionRate?: number;
  retrocessionAmount?: number;
  paymentStatus?: "waiting" | "paid" | "partial";
  paymentDate?: Date;
  paymentDueDate?: Date;
  paymentBaseDate?: Date;
  paymentTermsPolicy?: {
    baseDate: "reception" | "order" | "invoice";
    endOfMonth: boolean;
    extraDays: number;
  };
  retailerPaymentDelayDays?: number;
  supplierPaymentStatus?: "pending" | "paid" | "partial";
  retrocessionStatus?: "waiting_payment" | "waiting_retrocession" | "sent" | "received" | "partial";
  creationDate?: Date;
  lastUpdateDate?: Date;
  updatedAt?: Date;
  carrierId?: string;
  trackingNumber?: string;
  weightKg?: number;
  lastTrackingStatus?: string;
  lastTrackingAt?: Date;
  deliveredAt?: Date;
  estimatedDeliveryAt?: Date;
  parcelCount?: number;
  parcels?: { trackingNumber: string }[];
  type?: "inbound" | "outbound";
  sourceLocationId?: string;
  warehouseId?: string;
  sourceLocationName?: string;
  incomingPayment?: {
    source?: "manual" | "invoice";
    status?: "waiting" | "paid" | "partial";
    paidAt?: Date;
    dueAt?: Date;
    amount?: number;
    invoice?: LinkedInvoice | null;
  };
  outgoingPayment?: {
    source?: "manual" | "invoice";
    status?: "pending" | "paid" | "partial";
    paidAt?: Date;
    dueAt?: Date;
    amount?: number;
    invoice?: LinkedInvoice | null;
  };
}

export interface LinkedInvoice {
  id: string;
  invoiceId?: string;
  number?: string;
  invoiceNumber?: string;
  status?: string;
  amount?: number;
  currency?: string;
  issuedAt?: string | Date;
  dueAt?: string | Date;
  thirdpartyName?: string;
  sourceUrl?: string;
  kind: "customer" | "supplier";
  raw?: any;
}

export interface PennylaneInvoiceListResponse {
  kind: "customer" | "supplier";
  query: string;
  items: LinkedInvoice[];
}

export interface PurchaseOrderTrackingResponse {
  purchaseOrderId: string;
  carrierId: string;
  trackingNumber: string;
  tracking: {
    carrierId: string;
    trackingNumber: string;
    estimatedDeliveryDate: string | null;
    status: string | null;
    raw: any;
  };
  purchaseOrder?: PurchaseOrder | null;
}

export interface PurchaseOrderShippingEstimateQuote {
  serviceType: string | null;
  serviceName: string | null;
  currency: string | null;
  amounts: {
    totalNetCharge: number | null;
    totalBaseCharge: number | null;
    totalSurcharges: number | null;
    totalTaxes: number | null;
  };
  delivery: {
    deliveryTimestamp: any;
  };
  raw: any;
}

export interface PurchaseOrderShippingEstimateResponse {
  purchaseOrderId: string;
  carrierId: string;
  requestedShipment: any;
  quotes: PurchaseOrderShippingEstimateQuote[];
  raw: any;
  purchaseOrder?: PurchaseOrder | null;
}

export const getPurchaseOrderShippingEstimate = async (
  id: string
): Promise<PurchaseOrderShippingEstimateResponse> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/purchase-orders/${id}/shipping-estimate`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (handleUnauthorizedError(response)) {
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de l'estimation d'expédition du bon ${id}:`,
      error
    );
    throw error;
  }
};

export const classifySupplyInboxMessages = async (messages: any[]): Promise<any[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/supply-mail/classify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ messages }),
    });

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    const payload = await response.json();
    return Array.isArray(payload?.items) ? payload.items : [];
  } catch (error) {
    console.error("Erreur lors de la classification des emails supply:", error);
    throw error;
  }
};

export const fetchSupplyInboxMessages = async (top = 25): Promise<any[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/supply-mail/inbox?top=${encodeURIComponent(String(top))}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des emails supply:", error);
    throw error;
  }
};

// Obtenir tous les bons de commande
export const getAllPurchaseOrders = async (filters?: {
  reference?: string;
  supplier?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
  type?: "inbound" | "outbound";
}): Promise<PurchaseOrder[]> => {
  try {
    // Construire l'URL avec les paramètres de filtrage
    const url = new URL(
      `${API_BASE_URL}/purchase-orders`,
      window.location.origin
    );

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          url.searchParams.append(key, value);
        }
      });
    }

    console.log("Fetching purchase orders from URL:", url.toString());

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des bons de commande:",
      error
    );
    throw error;
  }
};

export const getPurchaseOrderTracking = async (
  id: string
): Promise<PurchaseOrderTrackingResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/purchase-orders/${id}/tracking`, {
      headers: getAuthHeaders(),
    });

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la récupération du tracking du bon ${id}:`, error);
    throw error;
  }
};

// Mettre à jour les lots (batches) d'un bon de commande à partir d'un tableau { barcode, batches }
export interface PurchaseOrderBatchLine {
  barcode: string;
  batches: {
    batchNumber: string;
    expirationDate?: string | Date;
    quantity: number;
  }[];
}

export const updatePurchaseOrderBatches = async (
  id: string,
  lines: PurchaseOrderBatchLine[]
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/purchase-orders/${id}/batches`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify(lines),
      }
    );

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    const text = await response.text();
    if (!response.ok) {
      try {
        const errorData = JSON.parse(text);
        throw new Error(
          `Erreur lors de la mise à jour des lots: ${
            errorData.message || response.statusText
          }${errorData.errors ? " - " + errorData.errors.join(", ") : ""}`
        );
      } catch {
        throw new Error(`Erreur HTTP: ${response.status} - ${text}`);
      }
    }

    return JSON.parse(text);
  } catch (error) {
    console.error(
      `Erreur lors de la mise à jour des lots du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

// Obtenir un bon de commande par ID
export const getPurchaseOrderById = async (
  id: string
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(`${API_BASE_URL}/purchase-orders/${id}`, {
      headers: getAuthHeaders(),
    });

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

// Créer un nouveau bon de commande
export const createPurchaseOrder = async (
  orderData: PurchaseOrder
): Promise<PurchaseOrder> => {
  try {
    // Vérification et préparation des données avant envoi
    if (!orderData.locationId) {
      throw new Error("L'ID de l'emplacement est requis");
    }

    // Vérifier que chaque article a un productName
    if (orderData.items && orderData.items.length > 0) {
      orderData.items.forEach((item, index) => {
        if (!item.productName) {
          throw new Error(
            `Le nom du produit est manquant pour l'article ${index + 1}`
          );
        }
      });
    }

    // Générer une référence temporaire si elle n'existe pas
    // Le backend devrait normalement le faire, mais ajoutons-le en sécurité
    if (!orderData.reference) {
      const date = new Date();
      const year = date.getFullYear().toString().slice(2);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const randomNum = Math.floor(Math.random() * 900 + 100); // Nombre aléatoire à 3 chiffres
      orderData.reference = `PO-${year}${month}-${randomNum}`;
    }

    // Log détaillé pour voir si le SKU est présent dans les items
    console.log(
      "Données du bon de commande envoyées:",
      JSON.stringify(orderData, null, 2)
    );

    // Log spécifique pour les codes-barres
    if (orderData.items && orderData.items.length > 0) {
      console.log("Codes-barres des produits dans la commande:");
      orderData.items.forEach((item, index) => {
        console.log(
          `Item ${index + 1}: productName=${item.productName}, barcode=${
            item.barcode
          }`
        );
      });
    }

    const response = await fetch(`${API_BASE_URL}/purchase-orders`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(orderData),
    });

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Erreur lors de la création du bon de commande: ${
          errorData.message || response.statusText
        }${errorData.errors ? " - " + errorData.errors.join(", ") : ""}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la création du bon de commande:", error);
    throw error;
  }
};

// Mettre à jour un bon de commande existant
export const updatePurchaseOrder = async (
  id: string,
  orderData: Partial<PurchaseOrder>
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(`${API_BASE_URL}/purchase-orders/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(orderData),
    });

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Erreur lors de la mise à jour du bon de commande: ${
          errorData.message || response.statusText
        }${errorData.errors ? " - " + errorData.errors.join(", ") : ""}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la mise à jour du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

// Mettre à jour le statut d'un bon de commande
export const updatePurchaseOrderStatus = async (
  id: string,
  status:
    | "draft"
    | "sent"
    | "partially_received"
    | "in_delivery"
    | "noted_delivered"
    | "received"
    | "completed"
    | "cancelled"
    | "dispute"
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/purchase-orders/${id}/status`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      }
    );

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la mise à jour du statut du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

// Envoyer automatiquement un bon de commande (notification à l'entrepôt)
export const sendPurchaseOrderNotification = async (
  id: string
): Promise<{
  purchaseOrder: PurchaseOrder;
  notifications: {
    sent: Array<{ email: string; label: string; notificationType: string }>;
    skipped: string[];
    failed: Array<{ email: string; label: string; error: string }>;
  };
}> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/purchase-orders/${id}/send`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      }
    );

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de l'envoi automatique du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

// Mettre à jour uniquement le coût d'importation d'un bon de commande
export const updatePurchaseOrderImportCost = async (
  id: string,
  importCost: number
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/purchase-orders/${id}/import-cost`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ importCost }),
      }
    );

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la mise à jour du coût d'importation du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};

export const getPennylaneInvoicesForPurchaseOrder = async (
  id: string,
  options: { kind: "customer" | "supplier"; query?: string; page?: number; perPage?: number }
): Promise<PennylaneInvoiceListResponse> => {
  try {
    const url = new URL(`${API_BASE_URL}/purchase-orders/${id}/pennylane/invoices`, window.location.origin);
    url.searchParams.set("kind", options.kind);
    if (options.query) url.searchParams.set("query", options.query);
    if (options.page) url.searchParams.set("page", String(options.page));
    if (options.perPage) url.searchParams.set("perPage", String(options.perPage));

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la récupération des factures Pennylane du bon ${id}:`, error);
    throw error;
  }
};

export const linkPennylaneInvoiceToPurchaseOrderPayment = async (
  id: string,
  payload: {
    direction: "incoming" | "outgoing";
    invoice?: LinkedInvoice | null;
    clear?: boolean;
    syncStatus?: boolean;
    manualStatus?: "waiting" | "pending" | "partial" | "paid";
    paidAt?: string;
  }
): Promise<PurchaseOrder> => {
  try {
    const response = await fetch(`${API_BASE_URL}/purchase-orders/${id}/payment-link`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Erreur lors du rattachement facture sur le bon ${id}:`, error);
    throw error;
  }
};

// Supprimer un bon de commande
export const deletePurchaseOrder = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/purchase-orders/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    // Gérer les erreurs d'authentification
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la suppression du bon de commande ${id}:`,
      error
    );
    throw error;
  }
};
